import { useId, useState } from "react";
import { AlertTriangle, CheckCircle2, ChevronDown } from "lucide-react";
import { parseScopeReviewNote, updateScopeReviewNote, type ScopeReviewIssue } from "@workspace/api-zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

export interface ManualScopeItem {
  name: string; description: string; quantity: number; unit: string; unitCost: number;
  category: string; labourHours: number; materialCost: number; isOptional: boolean;
}
export function QuoteScopeReview({ notes = [], onChange, onAdd, editHref, className = "my-4" }: {
  notes?: string[]; onChange?: (notes: string[]) => void;
  onAdd?: (item: ManualScopeItem) => void; editHref?: string; className?: string;
}) {
  const [expanded, setExpanded] = useState(false);
  const detailsId = useId();
  const statusId = useId();
  const issues = notes.flatMap(note => {
    const issue = parseScopeReviewNote(note);
    return issue ? [{ note, issue }] : [];
  });
  if (!issues.length) return null;
  const open = issues.filter(({ issue }) => !issue.resolved).length;
  return <section className={`${className} overflow-hidden rounded-lg border border-amber-300/40 text-xs dark:border-amber-800/40`}
    aria-label="Scope review" data-testid="quote-scope-review">
    <button type="button" className="flex w-full flex-wrap items-center gap-x-2 gap-y-1 bg-amber-50/40 px-2 py-2 sm:gap-x-3 sm:px-4 sm:py-3 text-left text-xs text-amber-800 hover:bg-amber-50/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring dark:bg-amber-950/20 dark:text-amber-300"
      aria-label={expanded ? "Hide Details" : "Show Details"} aria-describedby={statusId}
      aria-expanded={expanded} aria-controls={detailsId} onClick={() => setExpanded(value => !value)}>
      <span id={statusId} className="inline-flex items-center gap-2 font-medium">
        <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-amber-600 dark:text-amber-400" />
        {open ? `${open} scope ${open === 1 ? "issue" : "issues"} to check` : "Scope review — checked by you"}
      </span>
      {open > 0 && <span className={`${expanded ? "order-last basis-full sm:order-none sm:basis-auto" : "hidden sm:block"} min-w-0 flex-1 font-normal`}>Missing work may not be included in the total. You can continue with this quotation.</span>}
      <span className="ml-auto inline-flex shrink-0 items-center gap-2">
        {expanded ? "Hide Details" : "Show Details"}
        <ChevronDown className={`h-3.5 w-3.5 transition-transform ${expanded ? "rotate-180" : "-rotate-90"}`} />
      </span>
    </button>
    <div id={detailsId} hidden={!expanded} className="border-t border-amber-300/40 bg-muted/30 px-4 py-3 text-foreground dark:border-amber-800/40">
      <div className="divide-y divide-border">
        {issues.map(({ note, issue }) => <ScopeIssueRow key={`${issue.missingWork}\n${issue.sourceQuote}`}
          issue={issue} editable={!!onChange} canAdd={!!onAdd}
          onResolve={(resolved, resolution) => onChange?.(updateScopeReviewNote(notes, note, resolved, resolution))}
          onAdd={item => { onAdd?.(item); onChange?.(updateScopeReviewNote(notes, note, true, `Added manually: ${item.name}`)); }} />)}
      </div>
      {editHref && open > 0 && <a href={editHref} className="mt-3 inline-block underline">Edit quotation to address these issues</a>}
    </div>
  </section>;
}
function ScopeIssueRow({ issue, editable, canAdd, onResolve, onAdd }: {
  issue: ScopeReviewIssue; editable: boolean; canAdd: boolean;
  onResolve: (resolved: boolean, resolution?: string) => void; onAdd: (item: ManualScopeItem) => void;
}) {
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState(issue.missingWork.slice(0, 500));
  const [description, setDescription] = useState(issue.sourceQuote);
  const [quantity, setQuantity] = useState("1");
  const [unit, setUnit] = useState("item");
  const [price, setPrice] = useState("");
  const valid = name.trim() && unit.trim() && quantity.trim() && price.trim() &&
    Number.isFinite(Number(quantity)) && Number(quantity) > 0 &&
    Number.isFinite(Number(price)) && Number(price) >= 0;
  return <article className="space-y-2 py-3 first:pt-0 last:pb-0">
    <div className="flex min-w-0 items-start gap-2">
      {issue.resolved && <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-green-700" />}
      <p className="min-w-0 flex-1 break-words text-xs font-normal">{issue.missingWork}</p>
    </div>
    <div className="space-y-2 text-muted-foreground">
      {issue.sourceQuote && <blockquote className="border-l-2 border-amber-300 pl-2">
        Scope / work item reference: “{issue.sourceQuote}”</blockquote>}
      {issue.resolved ? <p>{issue.resolution || "Confirmed covered by the builder."}
        {" "}Reopening this issue does not remove any work item.</p> :
        <p>Add any required work manually, or confirm it is already covered. Confirming coverage does not add a cost.</p>}
    </div>
    {editable && <div className="flex flex-wrap items-center gap-2">
      {issue.resolved ? <Button type="button" variant="outline" size="sm" className="h-6 px-2 text-xs"
        onClick={() => { setAdding(false); onResolve(false); }}>Reopen issue</Button> :
        <><Button type="button" variant="outline" size="sm" className="h-6 px-2 text-xs"
          onClick={() => onResolve(true, "Builder confirmed the work is already covered.")}>Already covered</Button>
          {canAdd && <Button type="button" size="sm" variant="outline" className="h-6 px-2 text-xs"
            onClick={() => setAdding(value => !value)}>Add manually</Button>}</>}
    </div>}
    {adding && !issue.resolved && <div className="rounded-md border bg-background p-2 space-y-2 text-foreground">
      <label className="block space-y-1 text-xs">Work item name<Input className="h-7 px-2 py-1 text-xs md:text-xs" maxLength={500} value={name} onChange={e => setName(e.target.value)} /></label>
      <label className="block space-y-1 text-xs">Description / missing information<Textarea className="min-h-[48px] px-2 py-1 text-xs md:text-xs" rows={2} value={description} onChange={e => setDescription(e.target.value)} /></label>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
        <label className="block space-y-1 text-xs">Quantity<Input className="h-7 px-2 py-1 text-xs md:text-xs" type="number" min="0" step="any" value={quantity} onChange={e => setQuantity(e.target.value)} /></label>
        <label className="block space-y-1 text-xs">Unit<Input className="h-7 px-2 py-1 text-xs md:text-xs" maxLength={100} value={unit} onChange={e => setUnit(e.target.value)} /></label>
        <label className="block space-y-1 text-xs">Unit selling price (£)<Input className="h-7 px-2 py-1 text-xs md:text-xs" type="number" min="0" step="any" value={price} onChange={e => setPrice(e.target.value)} /></label>
      </div>
      <p className="text-xs text-muted-foreground">Enter your own price. The item is added to General; you can change its category and materials in the work item editor.</p>
      <Button type="button" size="sm" className="h-6 px-2 text-xs" disabled={!valid} onClick={() => {
        if (!valid) return;
        onAdd({ name: name.trim(), description: description.trim(), quantity: Number(quantity), unit: unit.trim(),
          unitCost: Number(price), category: "general", labourHours: 0, materialCost: 0, isOptional: false });
        setAdding(false);
      }}>Add to quotation</Button>
    </div>}
  </article>;
}
